import type { FeatureCollection, Geometry, Position } from "geojson";

import type { CorridorModel, Deployment, PlacementOrientation, TacticalGraphics } from "../../api/types";
import type { DispositifState } from "../dossierWrites";
import type { LonLat } from "../geo";

/** Aperçu du sens de la manœuvre (docs/14 §2.14, §2.5 de la spécification) : copie du déploiement dont tous les
 *  éléments dessinés passent par la transformation `m` (M = T(cible) ∘ T(courant)⁻¹, `orientation.transformOf`), les
 *  angles portés tournant de `deltaDeg` (θ cible − θ courant). Calculée côté client à chaque image, sans requête ;
 *  les calques de la carte la lisent par `useShownDeployment`, le reste de l'application garde le déploiement du
 *  serveur. Fonction pure : le déploiement reçu n'est jamais modifié.
 *
 *  Tournent : positions des unités (tous les temps), symboles de mission (position et rotation), limites, marques
 *  d'échelon, LIMA (tracés et centres), repère des glissements, modèle éditable et amorce de référence, lignes et
 *  zones de la ZO ; les graphiques tactiques (`graphics_features`) seulement avec `graphics`. Jamais les ponts
 *  détruits (état du terrain, dessinés par leur propre couche). */

export interface OrientOptions {
  /** Les graphiques tactiques tournent aussi (case « Faire tourner aussi les graphiques tactiques »). */
  graphics: boolean;
}

function turnAngle(deg: number, deltaDeg: number): number {
  return (((deg + deltaDeg) % 360) + 360) % 360;
}

function pair(m: (p: LonLat) => LonLat, p: readonly number[]): [number, number] {
  const [lon, lat] = m([p[0]!, p[1]!]);
  return [lon, lat];
}

function geometryOf(g: Geometry, m: (p: LonLat) => LonLat): Geometry {
  const pos = (p: Position): Position => pair(m, p);
  switch (g.type) {
    case "Point":
      return { ...g, coordinates: pos(g.coordinates) };
    case "MultiPoint":
    case "LineString":
      return { ...g, coordinates: g.coordinates.map(pos) };
    case "MultiLineString":
    case "Polygon":
      return { ...g, coordinates: g.coordinates.map((ring) => ring.map(pos)) };
    case "MultiPolygon":
      return { ...g, coordinates: g.coordinates.map((poly) => poly.map((ring) => ring.map(pos))) };
    case "GeometryCollection":
      return { ...g, geometries: g.geometries.map((child) => geometryOf(child, m)) };
  }
}

/** Collection GeoJSON transformée (graphiques tactiques). */
export function orientFeatures(collection: FeatureCollection, m: (p: LonLat) => LonLat): FeatureCollection {
  return {
    ...collection,
    features: collection.features.map((f) => (f.geometry ? { ...f, geometry: geometryOf(f.geometry, m) } : f)),
  };
}

function orientModel(model: CorridorModel, m: (p: LonLat) => LonLat): CorridorModel {
  return { ...model, left: model.left.map((p) => pair(m, p)), right: model.right.map((p) => pair(m, p)) };
}

export function orientDeployment(
  deployment: Deployment,
  m: (p: LonLat) => LonLat,
  deltaDeg: number,
  options: OrientOptions,
): Deployment {
  const units = deployment.units.map((unit) => {
    const positions: typeof unit.positions = {};
    for (const [phase, pos] of Object.entries(unit.positions) as [keyof typeof unit.positions, (typeof unit.positions)[keyof typeof unit.positions]][]) {
      if (!pos) continue;
      const [lon, lat] = m([pos.lon, pos.lat]);
      positions[phase] = { ...pos, lon, lat };
    }
    return { ...unit, positions };
  });
  const symbols: Deployment["mission_symbols"] = {};
  for (const [phase, list] of Object.entries(deployment.mission_symbols ?? {}) as [keyof Deployment["mission_symbols"], Deployment["mission_symbols"][keyof Deployment["mission_symbols"]]][]) {
    symbols[phase] = (list ?? []).map((s) => ({
      ...s,
      coordinates: pair(m, s.coordinates),
      rotation: turnAngle(s.rotation, deltaDeg),
    }));
  }
  const c = deployment.corridor;
  const corridor: Deployment["corridor"] = c
    ? {
        ...c,
        upper: c.upper.map((p) => pair(m, p)),
        lower: c.lower.map((p) => pair(m, p)),
        lines: c.lines.map((line) => ({
          ...line,
          coordinates: line.coordinates.map((p) => pair(m, p)),
          center: pair(m, line.center),
        })),
        marks: c.marks.map((mark) => ({ coordinates: pair(m, mark.coordinates), rotation: turnAngle(mark.rotation, deltaDeg) })),
        track: { ...c.track, origin: pair(m, c.track.origin), axis: turnAngle(c.track.axis, deltaDeg) },
        model: c.model ? orientModel(c.model, m) : c.model,
        reference_seed: c.reference_seed
          ? { left: c.reference_seed.left.map((p) => pair(m, p)), right: c.reference_seed.right.map((p) => pair(m, p)) }
          : c.reference_seed,
        measures: c.measures
          ? {
              ...c.measures,
              lines: c.measures.lines.map((line) => ({ ...line, coordinates: line.coordinates.map((p) => pair(m, p)) })),
              zones: c.measures.zones.map((zone) => ({ ...zone, label: pair(m, zone.label) })),
            }
          : c.measures,
      }
    : c;
  const graphics =
    options.graphics && deployment.graphics_features ? orientFeatures(deployment.graphics_features, m) : deployment.graphics_features;
  return { ...deployment, units, mission_symbols: symbols, corridor, graphics_features: graphics };
}

/** État du dispositif à envoyer avec un nouveau sens (§1.4 de la spécification) : positions posées à la main (tous
 *  les temps, unités hors gabarit de l'ODB ennemi comprises) et limites du fuseau retouché tournées d'un bloc par M ;
 *  LIMA en (s, t), courbures et GDH saisis inchangés ; le reste tel quel. */
export function orientState(
  state: DispositifState,
  m: (p: LonLat) => LonLat,
  orientation: PlacementOrientation | null,
): DispositifState {
  const positions: DispositifState["positions"] = {};
  for (const [key, perPhase] of Object.entries(state.positions)) {
    const out: (typeof positions)[string] = {};
    for (const [phase, point] of Object.entries(perPhase ?? {}) as [keyof typeof out, [number, number] | undefined][]) {
      if (point) out[phase] = pair(m, point);
    }
    positions[key] = out;
  }
  return { ...state, positions, corridor: state.corridor ? orientModel(state.corridor, m) : null, orientation };
}

/** Graphiques tactiques tournés avec la manœuvre : zones (centre et sommets, rayon inchangé), lignes (sommets),
 *  panaches NRBC (point de rejet, dérive tournée de `deltaDeg`). */
export function orientGraphics(graphics: TacticalGraphics, m: (p: LonLat) => LonLat, deltaDeg: number): TacticalGraphics {
  const at = (latitude: number, longitude: number) => {
    const [lon, lat] = m([longitude, latitude]);
    return { latitude: lat, longitude: lon };
  };
  return {
    operationalGraphics: graphics.operationalGraphics.map((g) => ({
      ...g,
      ...at(g.latitude, g.longitude),
      ...(g.ring ? { ring: g.ring.map((c) => at(c.latitude, c.longitude)) } : {}),
    })),
    lineGraphics: graphics.lineGraphics.map((g) => ({ ...g, coordinates: g.coordinates.map((c) => at(c.latitude, c.longitude)) })),
    cbrnPlumes: graphics.cbrnPlumes.map((g) => {
      const [releaseLon, releaseLat] = m([g.releaseLon, g.releaseLat]);
      return { ...g, releaseLat, releaseLon, plumeBearingDegrees: turnAngle(g.plumeBearingDegrees, deltaDeg) };
    }),
  };
}

/** Points des graphiques tactiques (centres, sommets, points de rejet) : contrôle des sens possibles. */
export function graphicPoints(graphics: TacticalGraphics): LonLat[] {
  return [
    ...graphics.operationalGraphics.flatMap((g) => [
      [g.longitude, g.latitude] as LonLat,
      ...(g.ring ?? []).map((c) => [c.longitude, c.latitude] as LonLat),
    ]),
    ...graphics.lineGraphics.flatMap((g) => g.coordinates.map((c) => [c.longitude, c.latitude] as LonLat)),
    ...graphics.cbrnPlumes.map((g) => [g.releaseLon, g.releaseLat] as LonLat),
  ];
}

/** Nombre de graphiques tactiques. */
export function graphicsCount(graphics: TacticalGraphics): number {
  return graphics.operationalGraphics.length + graphics.lineGraphics.length + graphics.cbrnPlumes.length;
}

/** Positions posées à la main (tous les temps) : contrôle des sens possibles, nombre annoncé. */
export function handPoints(state: Pick<DispositifState, "positions">): LonLat[] {
  return Object.values(state.positions).flatMap((perPhase) =>
    Object.values(perPhase ?? {}).filter((p): p is [number, number] => Array.isArray(p)),
  );
}

/** Unités posées à la main (au moins un temps). */
export function handUnits(state: Pick<DispositifState, "positions">): number {
  return Object.values(state.positions).filter((perPhase) => Object.values(perPhase ?? {}).some(Boolean)).length;
}
